import { APP } from '../tokens';
import type { ReviewSuggestion } from '../../../types/review';

const F = APP.fontFamily;

// ─── Priority badge config ──────────────────────

const PRIORITY_LABEL: Record<string, string> = { high: 'HIGH', medium: 'MED', low: 'LOW' };

function PriorityBadge({ priority }: { priority: ReviewSuggestion['priority'] }) {
  const isHigh = priority === 'high';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 3,
        paddingBlock: 2,
        paddingInline: 6,
        backgroundColor: isHigh ? APP.bgReviewPriorityHigh : APP.bgReviewTag,
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: '0.06em',
        color: isHigh ? APP.textReviewPriorityHigh : APP.textReviewMuted,
        lineHeight: '12px',
        fontFamily: F,
        flexShrink: 0,
      }}
    >
      {PRIORITY_LABEL[priority]}
    </span>
  );
}

// ─── SVG icons ──────────────────────────────────

function CheckmarkIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={APP.textReviewAccepted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function DismissIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={APP.textReviewDismissed} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width={12} height={12} viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
      <circle cx="8" cy="8" r="7" stroke={APP.textReviewInfo} strokeWidth="1.2" />
      <line x1="8" y1="7" x2="8" y2="11" stroke={APP.textReviewInfo} strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="8" cy="5" r="0.5" fill={APP.textReviewInfo} />
    </svg>
  );
}

// ─── Card component ─────────────────────────────

// ─── Locate icon (crosshair) ────────────────────
function LocateIcon() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#D97645" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10" />
      <line x1="22" y1="12" x2="18" y2="12" />
      <line x1="6" y1="12" x2="2" y2="12" />
      <line x1="12" y1="6" x2="12" y2="2" />
      <line x1="12" y1="22" x2="12" y2="18" />
    </svg>
  );
}

interface ReviewCardProps {
  suggestion: ReviewSuggestion;
  isActive: boolean;
  onSelect: () => void;
  onAccept: () => void;
  onDismiss: () => void;
  /** When true, shows "Source excerpt" instead of "Original" with no strikethrough */
  clauseReviewMode?: boolean;
  /** Called when user clicks "Locate in document" */
  onLocate?: (flagId: string, quote: string, pageHint: number | null, sectionRef: string, sectionTitle: string) => void;
  /** Page hint for this flag (from ClauseFlag.pageHint) */
  pageHint?: number | null;
  /** Grounding status for this flag (populated after locateFlag) */
  groundingStatus?: 'exact' | 'approx' | 'missing' | 'unknown' | null;
}

export default function ReviewCard({ suggestion, isActive, onSelect, onAccept, onDismiss, clauseReviewMode, onLocate, pageHint, groundingStatus }: ReviewCardProps) {
  const { status } = suggestion;

  // ── Accepted state ─────────────────────────────
  if (status === 'accepted') {
    const acceptedLabel = suggestion.appliedToDocument
      ? 'Applied to document'
      : suggestion.applyFailed
        ? 'Apply failed'
        : 'Accepted';
    const acceptedColor = suggestion.applyFailed ? APP.textReviewPriorityHigh : APP.textReviewAccepted;
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 8,
          paddingBlock: 10,
          paddingInline: 12,
          gap: 6,
          backgroundColor: suggestion.applyFailed ? '#FFF3EE' : APP.bgReviewCardAccepted,
          border: `1px solid ${suggestion.applyFailed ? '#E8C4B0' : APP.borderReviewCardAccepted}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <CheckmarkIcon />
            <span style={{ fontSize: 11, fontWeight: 500, color: acceptedColor, lineHeight: '14px', fontFamily: F }}>
              §{suggestion.sectionRef} {suggestion.sectionTitle}
            </span>
          </div>
          <span style={{ fontSize: 9, fontWeight: 500, color: acceptedColor, lineHeight: '12px', fontFamily: F }}>
            {acceptedLabel}
          </span>
        </div>
        {suggestion.applyFailed && (
          <span style={{ fontSize: 10, fontWeight: 400, color: '#A83A1F', lineHeight: '14px', fontFamily: F }}>
            {suggestion.applyFailed}
          </span>
        )}
      </div>
    );
  }

  // ── Dismissed state ────────────────────────────
  if (status === 'dismissed') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          opacity: 0.6,
          borderRadius: 8,
          paddingBlock: 10,
          paddingInline: 12,
          gap: 6,
          backgroundColor: APP.bgReviewCardDismissed,
          border: `1px solid ${APP.borderReviewCardDismissed}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <DismissIcon />
            <span
              style={{
                fontSize: 11,
                fontWeight: 500,
                color: APP.textReviewDismissed,
                lineHeight: '14px',
                fontFamily: F,
                textDecoration: 'line-through',
              }}
            >
              §{suggestion.sectionRef} {suggestion.sectionTitle}
            </span>
          </div>
          <span style={{ fontSize: 9, fontWeight: 500, color: APP.textReviewDismissed, lineHeight: '12px', fontFamily: F }}>
            Dismissed
          </span>
        </div>
      </div>
    );
  }

  // ── Active (expanded) state ────────────────────
  const dotColor = suggestion.priority === 'high' ? APP.textReviewPriorityHigh : '#B8A99A';

  if (isActive) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 8,
          paddingBlock: 12,
          paddingInline: 12,
          gap: 10,
          backgroundColor: APP.bgReviewCardActive,
          border: `1.5px solid ${APP.borderReviewCardActive}`,
        }}
      >
        {/* Header: dot + title + priority badge */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: dotColor,
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: 11, fontWeight: 600, color: APP.textDark, lineHeight: '14px', fontFamily: F }}>
              §{suggestion.sectionRef} {suggestion.sectionTitle}
            </span>
          </div>
          <PriorityBadge priority={suggestion.priority} />
        </div>

        {/* Summary */}
        <span style={{ fontSize: 12, fontWeight: 500, color: APP.textDark, lineHeight: '16px', fontFamily: F }}>
          {suggestion.summary}
        </span>

        {/* Source excerpt (clause review) or Original (standard review) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span
              style={{
                fontSize: 9,
                fontWeight: 500,
                letterSpacing: '0.05em',
                color: clauseReviewMode ? '#D97645' : APP.textReviewMuted,
                lineHeight: '12px',
                fontFamily: F,
                textTransform: 'uppercase',
              }}
            >
              {clauseReviewMode ? 'Source excerpt' : 'Original'}
              {clauseReviewMode && pageHint != null && (
                <span style={{ fontWeight: 400, letterSpacing: '0.02em', color: APP.textReviewMuted, textTransform: 'none' }}>
                  {' '}· p.{pageHint}
                </span>
              )}
            </span>
            {clauseReviewMode && onLocate && (
              <button
                type="button"
                title="Locate in document"
                onClick={(e) => { e.stopPropagation(); onLocate(suggestion.id, suggestion.originalText, pageHint ?? null, suggestion.sectionRef, suggestion.sectionTitle); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '2px 6px', borderRadius: 4,
                  backgroundColor: 'transparent', border: '1px solid #E8DED4',
                  cursor: 'pointer', fontSize: 9, fontWeight: 500,
                  color: '#D97645', fontFamily: F, lineHeight: '12px',
                }}
              >
                <LocateIcon />
                Locate
              </button>
            )}
            {clauseReviewMode && groundingStatus && groundingStatus !== 'unknown' && (
              <span
                title={
                  groundingStatus === 'exact' ? 'Located precisely in document'
                  : groundingStatus === 'approx' ? 'Approximately located — verify in context'
                  : 'Could not be located — review manually'
                }
                style={{
                  fontSize: 8, fontWeight: 500, letterSpacing: '0.03em',
                  color: groundingStatus === 'exact' ? '#4A9E6B' : groundingStatus === 'approx' ? '#B5930E' : '#C04A2A',
                  fontFamily: F, lineHeight: '12px',
                }}
              >
                {groundingStatus === 'exact' ? 'Located' : groundingStatus === 'approx' ? 'Approx' : 'Not found'}
              </span>
            )}
          </div>
          <span
            style={{
              fontSize: 11,
              fontWeight: 400,
              color: clauseReviewMode ? '#6B5E52' : APP.textReviewMuted,
              lineHeight: '16px',
              fontFamily: '"Georgia", serif',
              textDecoration: clauseReviewMode ? 'none' : 'line-through',
              ...(clauseReviewMode ? {
                borderLeft: '2px solid #D97645',
                paddingLeft: 8,
              } : {}),
            }}
          >
            {suggestion.originalText}
          </span>
        </div>

        {/* PROPOSED section */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span
            style={{
              fontSize: 9,
              fontWeight: 500,
              letterSpacing: '0.05em',
              color: APP.textReviewProposed,
              lineHeight: '12px',
              fontFamily: F,
              textTransform: 'uppercase',
            }}
          >
            Proposed
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 400,
              color: APP.textDark,
              lineHeight: '16px',
              fontFamily: '"Georgia", serif',
            }}
          >
            {suggestion.proposedText}
          </span>
        </div>

        {/* Info callout */}
        {suggestion.rationale && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              borderRadius: 4,
              paddingBlock: 6,
              paddingInline: 8,
              gap: 5,
              backgroundColor: '#F5F2EC',
            }}
          >
            <InfoIcon />
            <span style={{ fontSize: 10, fontWeight: 400, color: APP.textReviewInfo, lineHeight: '15px', fontFamily: F }}>
              {suggestion.rationale}
            </span>
          </div>
        )}

        {/* Deviation note (playbook comparison hint) */}
        {suggestion.deviationNote && clauseReviewMode && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              borderRadius: 4,
              paddingBlock: 4,
              paddingInline: 8,
              backgroundColor: '#FFF8F0',
              border: '1px solid #F0DBC8',
            }}
          >
            <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#D97645" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            <span style={{ fontSize: 10, fontWeight: 500, color: '#C0612A', lineHeight: '14px', fontFamily: F }}>
              {suggestion.deviationNote}
            </span>
          </div>
        )}

        {/* Accept / Dismiss buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn-hover"
            onClick={onAccept}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 5,
              paddingBlock: 5,
              paddingInline: 14,
              backgroundColor: APP.textDark,
              border: 'none',
              cursor: 'pointer',
              fontFamily: F,
              fontSize: 11,
              fontWeight: 500,
              color: '#FDFCFB',
              lineHeight: '14px',
            }}
          >
            Accept suggestion
          </button>
          <button
            type="button"
            className="btn-hover"
            onClick={onDismiss}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 5,
              paddingBlock: 5,
              paddingInline: 14,
              backgroundColor: '#FDFCFB',
              border: `1px solid ${APP.borderReviewCardDefault}`,
              cursor: 'pointer',
              fontFamily: F,
              fontSize: 11,
              fontWeight: 500,
              color: APP.textReviewInfo,
              lineHeight: '14px',
            }}
          >
            Dismiss
          </button>
        </div>

        {/* Tags */}
        {suggestion.tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: -4 }}>
            {suggestion.tags.map((tag) => (
              <span
                key={tag}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 3,
                  paddingBlock: 2,
                  paddingInline: 6,
                  gap: 3,
                  backgroundColor: APP.bgReviewTag,
                  fontSize: 9,
                  fontWeight: 400,
                  color: APP.textReviewInfo,
                  lineHeight: '12px',
                  fontFamily: F,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Collapsed (pending, not active) ────────────
  return (
    <div
      className="card-hover"
      onClick={onSelect}
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 8,
        paddingBlock: 10,
        paddingInline: 12,
        gap: 6,
        backgroundColor: APP.bgReviewCardDefault,
        border: `1px solid ${APP.borderReviewCardDefault}`,
        cursor: 'pointer',
      }}
    >
      {/* Header: dot + title + priority badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: dotColor,
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 11, fontWeight: 600, color: APP.textDark, lineHeight: '14px', fontFamily: F }}>
            §{suggestion.sectionRef} {suggestion.sectionTitle}
          </span>
        </div>
        <PriorityBadge priority={suggestion.priority} />
      </div>

      {/* Summary */}
      <span style={{ fontSize: 11, fontWeight: 400, color: APP.textReviewInfo, lineHeight: '16px', fontFamily: F }}>
        {suggestion.summary}
      </span>
    </div>
  );
}
